import type { SizeData } from '@design-system/components/Pdp/PdpFitFinder.props'

/**
 * Represents a country with a key and label
 * @typedef {Object} Country
 * @property {string} key - Unique identifier for the country
 * @property {string} label - Display name of the country
 */
type Country = {
  key: string
  label: string
}

/**
 * Represents a category with a name and subcategories
 * @typedef {Object} Category
 * @property {string} name - Name of the category
 * @property {string[]} subcategories - List of subcategory size codes
 */
type Category = {
  name: string
  subcategories: string[]
}

/**
 * Utility composable for size-related operations
 *
 * Provides methods for size conversion, categorization, and internationalization
 *
 * @param {Map<string, SizeData>} sizes - Map of size codes to size data
 * @param {Country[]} countries - List of countries
 * @param {Category[]} categories - List of size categories
 *
 * @returns {Object} Size utility methods
 * @property {Country[]} countries - Original list of countries
 * @property {Category[]} categories - Original list of categories
 * @property {Function} getSizeTabs - Generate size tabs for different countries
 *
 * @example
 * const { getSizeTabs } = useSizeUtils(sizesMap, countriesList, categoriesList)
 */
export const useSizeUtils = (
  sizes: Map<string, SizeData>,
  countries: Country[],
  categories: Category[]
) => {
  /**
   * Converts a size code to a country-specific size
   *
   * @param {Country} country - Country to convert size for
   * @param {string} sizeCode - Original size code
   * @returns {string} Country-specific size representation
   * @throws {Error} If size conversion is not found
   *
   * @example
   * const italianSize = getSizeFromCountry({ key: 'IT', label: 'Italy' }, 'S')
   */
  const getSizeFromCountry = (country: Country, sizeCode: string) => {
    const size = sizes.get(sizeCode)
    const countryKey = country.key + '_ALS'
    if (size) {
      if (countryKey in Object.keys(size)) return size[countryKey]
      return size.TGL_COD
    } else {
      throw new Error('Size conversion not found')
    }
  }

  /**
   * Generates size tabs for different countries with categorized and localized sizes
   *
   * @template Item
   * @param {Item[]} items - List of size items to categorize
   * @returns {Array<{country: string, disabled: boolean, categories: Array<{name: string, options: Item[]}>}>}
   * Categorized and localized size tabs
   *
   * @description
   * - Sorts categories alphabetically
   * - Filters items by available sizes
   * - Groups sizes by category and country
   * - Handles uncategorized "Other" sizes
   *
   * @example
   * const sizeTabs = getSizeTabs([{ value: 'S' }, { value: 'M' }])
   */
  const getSizeTabs = <Item extends { value: string }>(items: Item[]) => {
    // Sort categories alphabetically
    categories.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )

    // Filter items to only include those with size data
    let filteredItems = items.filter(item => sizes.get(item.value))

    // Categorize items by size grid
    const categorizedItems = categories.map(category => {
      const filteredOptions: Item[] = []

      // Filter items into category or keep in remaining items
      filteredItems = filteredItems.filter(item => {
        if (
          category.subcategories.includes(
            sizes.get(item.value)!.SizeGrid_Code as string
          )
        ) {
          filteredOptions.push(item)
          return false
        }
        return true
      })

      // Sort options within category by order
      filteredOptions.sort(
        (opt1, opt2) =>
          sizes.get(opt1.value)!.TGL_ORD! - sizes.get(opt2.value)!.TGL_ORD!
      )

      return {
        name: category.name,
        options: filteredOptions,
      }
    })

    // Add "Other" category for uncategorized sizes
    if (filteredItems.length) {
      categorizedItems.push({
        name: 'Other',
        options: filteredItems,
      })
    }

    // Map categories to country-specific size labels
    return countries.map(country => {
      const mappedCategories = categorizedItems.map(category => {
        const mappedOptions = category.options.map(option => {
          const mappedSize = getSizeFromCountry(country, option.value)

          return {
            ...option,
            label: mappedSize,
          }
        })

        return {
          ...category,
          options: mappedOptions,
        }
      })

      // Remove categories with no options
      const removedEmptyCategories = mappedCategories.filter(
        mappedCategory => mappedCategory.options.length > 0
      )

      return {
        country: country.label,
        disabled: false,
        categories: removedEmptyCategories,
      }
    })
  }

  return {
    countries,
    categories,
    getSizeTabs,
  }
}
